import { makeAutoObservable, runInAction } from 'mobx'
import { TEXT_TO_SPEECH_AUDIO_NAME } from 'shared/constants/limits'
import {
  type IParamsGetSpeechPreview,
  type IResponseVoice,
  type ISpeechPreview,
  TextToSpeechApi,
} from 'entities/TextToSpeech'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type MessageData } from 'entities/Message'
import { RenameModalStore } from 'widgets/RenameModal'
import { TextToSpeechTextStore } from 'widgets/TextToSpeech/ui/TextToSpeechText/store/TextToSpeechTextStore'
import { TextToSpeechVoiceConfigDefault } from 'widgets/TextToSpeech/ui/TextToSpeechVoice/store/TextToSpeechVoiceConfigDefault'

export type ICreateTextToSpeechStoreProps = {
  type: 'create'
  onClose: () => void
  onCreateSpeech: (file: File) => void
  group?: 'broadcast' | 'voicemail'
}

export type IViewTextToSpeechStoreProps = {
  type: 'view'
  message: string
}

export type ITextToSpeechWidgetStoreConfigStore = {
  init: () => Promise<void>
  loadAudioSpeech: (params: IParamsGetSpeechPreview) => Promise<ISpeechPreview | void>
  getSpeechPreview: (key: string) => ISpeechPreview | undefined
}

export type ITextToSpeechStoreProps = (
  | ICreateTextToSpeechStoreProps
  | IViewTextToSpeechStoreProps
) & {
  voiceId?: string
  getConfigStore?: (parent: {
    setSelectedVoice: (voice: IResponseVoice) => void
    selectedVoice: IResponseVoice | null
  }) => ITextToSpeechWidgetStoreConfigStore
}

export class TextToSpeechWidgetStore {
  readonly configStore: ITextToSpeechWidgetStoreConfigStore
  readonly initVoiceId?: string
  readonly textToSpeechTextStore: TextToSpeechTextStore
  readonly type: ITextToSpeechStoreProps['type']
  readonly createTextToSpeechStoreProps: ICreateTextToSpeechStoreProps | null = null
  constructor(
    { getConfigStore, ...props }: ITextToSpeechStoreProps,
    private _entities: IEntitiesStore
  ) {
    if (getConfigStore) {
      this.configStore = getConfigStore(this)
    } else {
      this.configStore = new TextToSpeechVoiceConfigDefault(_entities, this)
    }
    this.initVoiceId = props.voiceId
    this.textToSpeechTextStore = new TextToSpeechTextStore(_entities, props)
    this.type = props.type
    if (props.type === 'create') {
      this.createTextToSpeechStoreProps = props
    }
    if (props.type === 'view') {
      this.setMessageData({
        message: props.message,
        isReplace: true,
        attachments: [],
      })
    }

    makeAutoObservable(this)
  }

  get params() {
    const voiceId = (this._initLoading && this.initVoiceId) || this.selectedVoice?.id
    return {
      voiceId,
      text: this.textToSpeechTextStore.text,
    }
  }

  reset = () => {
    this.selectedVoice = null
    this.setMessageData({
      message: '',
      isReplace: true,
      attachments: [],
    })
  }

  private _initLoading = true
  init = async () => {
    await this.configStore.init()

    runInAction(() => {
      this._initLoading = false
    })
  }

  private _isHideVoicesDropdown = false

  selectedVoice: IResponseVoice | null = null
  setSelectedVoice = (item: IResponseVoice) => {
    this.selectedVoice = item
  }

  get error() {
    return this.textToSpeechTextStore.error
  }

  get isErrorLength() {
    return this.text.trim().length > 2160
  }

  get text() {
    return this.textToSpeechTextStore.text
  }

  get textKey() {
    return `${this.selectedVoice?.id}_${this.text.trim()}`
  }

  get isDisabledCreate() {
    return !!this.error
  }

  get isEmpty() {
    return !this.text.trim()
  }

  get isHideVoicesDropdown() {
    return this._isHideVoicesDropdown
  }

  get isDisabledPreview() {
    return !this.selectedVoice || this.isEmpty || this.isErrorLength
  }

  loadAudioSpeech = async () => {
    if (!this.selectedVoice?.id) {
      return
    }
    if (this.isEmpty) {
      return
    }
    const params = {
      voice_id: this.selectedVoice?.id,
      text: this.text.trim(),
    }
    await this.configStore.loadAudioSpeech(params)
  }

  get audioSpeech() {
    return this.configStore.getSpeechPreview(this.textKey)
  }

  get audioSpeechSrc() {
    return this.audioSpeech?.src
  }

  get audioFileDuration() {
    return this.audioSpeech?.duration
  }

  get duration() {
    return Math.ceil(this.text.trim().length / 12)
  }

  setIsHideVoicesDropdown = (value: boolean) => {
    this._isHideVoicesDropdown = value
  }

  setMessageData = (props: MessageData) => {
    this.textToSpeechTextStore.setMessageData(props)
  }

  setValidError = (error: string) => {
    this.textToSpeechTextStore.setValidError(error)
  }

  clearError = () => {
    this.textToSpeechTextStore.clearError()
  }

  onCreate = () => {
    this.setValidError('')
    if (this.isEmpty) {
      this.setValidError('Message cannot be empty')
    }
    if (this.error) return
    new RenameModalStore({
      title: 'Audio name',
      element: 'Audio',
      maxNameLength: TEXT_TO_SPEECH_AUDIO_NAME,
      onSave: async (name: string) => {
        if (!this.selectedVoice?.id) {
          return
        }

        const {
          data: { payload },
        } = await TextToSpeechApi.createSpeech({
          voice_id: this.selectedVoice?.id,
          text: this.text.trim(),
          title: `${name}.mp3`,
          group: this.createTextToSpeechStoreProps?.group || 'broadcast',
        })
        const { data } = await TextToSpeechApi.streamSpeech(payload.id)
        const file = data

        if (file) {
          const audio = new File([file], `${name}.mp3`, {
            type: file.type,
            lastModified: file.lastModified,
          })
          this.createTextToSpeechStoreProps?.onCreateSpeech?.(audio)
          this.createTextToSpeechStoreProps?.onClose?.()
        }
      },
    })
  }
}
